import { Injectable } from '@angular/core';
import { getAiForm, validateAiFormValues } from '../cyn/fck-chat-module/core/forms/ai-form-catalog';
import { AiFormDrafts, AI_FORM_QUERY } from '../cyn/fck-chat-module/core/forms/ai-form-drafts';
import { AiFormAdapter, AiFormCreationCheck, AiFormCreationTarget, AiFormDraft, AiFormElement, AiFormField, AiFormOptionsQuery, AiFormRequests, AiFormState } from '../cyn/fck-chat-module/core/forms/ai-form.types';
import { describeAiFormFields, isAiFormPage, readAiFormValues, resolveAiFormLookupField, writeAiFormValues } from '../cyn/fck-chat-module/core/forms/ai-form-values';
import { getSessionSignature } from './ai-chat-auth-session';
import { loadGrantedPermissions } from '../cyn/fck-chat-module/core/pages/user-permissions';

const AI_FORM_CALL_QUERY = 'aiFormCall';
const FORM_WAIT_MS = 30_000;

export type PreparedAiForms =
    | { mode: 'single' | 'table'; form: unknown; reviewRequired?: boolean }
    | { mode: 'multiple'; drafts: AiFormDraft[] };

@Injectable({ providedIn: 'root' })
export class AgentFormService {
    readonly drafts = new AiFormDrafts(window.localStorage, getSessionSignature);
    private applied: AiFormAdapter | null = null;
    private restoreTask: Promise<void> | null = null;
    private activeDraft: AiFormDraft | null = null;
    private fillError: string | null = null;
    private readonly openers = new Map<string, () => Promise<unknown>>();
    private readonly preparedTables = new Map<string, AiFormAdapter>();
    private readonly preparedSingles = new Map<string, AiFormAdapter>();
    private readonly formInstanceIds = new WeakMap<AiFormAdapter, string>();
    private readonly creationCallIds = new WeakMap<AiFormAdapter, string>();
    private preparingTable = false;

    registerOpener(formId: string, open: () => Promise<unknown>): void {
        getAiForm(formId);
        this.openers.set(formId, open);
    }

    start(): void {
        document.addEventListener('cynteka-ai-form-ready', this.restore);
        document.addEventListener('cynteka-ai-form-change', this.saveCurrent);
        this.restore();
    }

    stop(): void {
        document.removeEventListener('cynteka-ai-form-ready', this.restore);
        document.removeEventListener('cynteka-ai-form-change', this.saveCurrent);
    }

    adapter(): AiFormAdapter | null {
        return Array.from(document.querySelectorAll<AiFormElement>('form, [data-ai-form]'))
            .map(element => element.aiFormAdapter).find(adapter => {
                if (!adapter) return false;
                const form = getAiForm(adapter.form);
                return !form.pageOnly || isAiFormPage(form);
            }) ?? null;
    }

    async snapshot(): Promise<unknown> {
        const adapter = this.adapter();
        if (!adapter) return null;
        let formInstanceId = this.formInstanceIds.get(adapter);
        if (!formInstanceId) {
            formInstanceId = crypto.randomUUID();
            this.formInstanceIds.set(adapter, formInstanceId);
        }
        const state = adapter.read();
        const { options, metadata, ...publicState } = state;
        const form = getAiForm(adapter.form);
        const fields = await this.fields(form.id, state.values, state);
        return {
            form: form.id, fields: describeAiFormFields(fields), ...publicState,
            values: readAiFormValues(fields, state.values, state),
            editableFields: fields.filter(field => !field.readOnly
                && (!state.editableFields || state.editableFields.includes(field.modelKey ?? field.key)))
                .map(field => field.key),
            formInstanceId,
            creationCallId: this.creationCallIds.get(adapter) ?? null,
            draftId: this.activeDraft?.id ?? null,
            fillError: this.fillError,
            confirmation: 'Для сохранения пользователь должен нажать «Сохранить».',
        };
    }

    async currentForm(): Promise<unknown> {
        const adapter = this.adapter();
        if (adapter) await this.waitForAdapter(adapter.form);
        await this.restore();
        return this.snapshot();
    }

    async checkCreation(formId: string, values: unknown, signal?: AbortSignal, resolvedFields?: AiFormField[],
        requests: AiFormRequests = new Map()): Promise<AiFormCreationCheck> {
        try {
            const form = getAiForm(formId);
            if (!(await loadGrantedPermissions()).has(form.permission)) throw new Error('Нет права создавать объекты этой формы');
            const fields = resolvedFields ?? await this.fields(formId, values, undefined, signal, requests);
            const target = await this.resolveCreationRoute(formId, validateAiFormValues(fields, values), signal, undefined, true, requests);
            return { available: true, ...(target.context ? { context: target.context } : {}) };
        } catch (error) {
            if (signal?.aborted) throw error;
            return { available: false, reason: error.message };
        }
    }

    private async readValues(adapter: AiFormAdapter): Promise<Record<string, unknown>> {
        const state = adapter.read();
        const fields = await this.fields(adapter.form, state.values, state);
        return readAiFormValues(fields, state.values, state);
    }

    async fields(formId: string, values: unknown = {}, state?: AiFormState, signal?: AbortSignal,
        requests?: AiFormRequests): Promise<AiFormField[]> {
        const definition = getAiForm(formId);
        if (!values || typeof values !== 'object' || Array.isArray(values)) throw new Error('values должен быть объектом');
        return definition.resolveFields ? definition.resolveFields(definition.fields,
            { values: values as Record<string, unknown>, state, signal, requests }) : definition.fields;
    }

    async schemaFields(formId: string, values?: unknown, signal?: AbortSignal, requests?: AiFormRequests): Promise<AiFormField[]> {
        const adapter = values === undefined ? this.adapter() : null;
        const state = adapter?.form === formId ? (await this.waitForAdapter(formId, signal)).read() : undefined;
        return this.fields(formId, values === undefined ? state?.values ?? {} : values, values === undefined ? state : undefined, signal, requests);
    }

    async fieldOptions(formId: string, path: string, query: AiFormOptionsQuery, signal?: AbortSignal, values?: unknown): Promise<unknown> {
        const definition = getAiForm(formId);
        if (!(await loadGrantedPermissions()).has(definition.permission)) throw new Error('Нет доступа к справочникам формы');
        if (typeof path !== 'string' || path.length > 160) throw new Error('Некорректный путь поля');
        if ((query.search != null && (typeof query.search !== 'string' || query.search.length > 200))
            || (query.page != null && (!Number.isInteger(query.page) || query.page < 1 || query.page > 10_000))) {
            throw new Error('Некорректные параметры поиска');
        }
        const adapter = values === undefined ? this.adapter() : null;
        const state = adapter?.form === formId ? (await this.waitForAdapter(formId, signal)).read() : undefined;
        const fields = await this.fields(formId, values === undefined ? state?.values ?? {} : values, state, signal);
        const resolved = resolveAiFormLookupField(fields, path);
        const field = resolved.field;
        const result = await field.caller(query, { state, signal });
        const option = field.processing?.option ?? (item => ({ id: String(item.id), label: String(item.name) }));
        return { field: resolved.path, items: result.items.map(option), hasMore: result.hasMore, page: query.page ?? 1 };
    }

    async patch(formId: string, values: unknown, signal?: AbortSignal): Promise<unknown> {
        const adapter = this.adapter();
        if (!adapter || adapter.form !== formId) throw new Error('Нужная форма не открыта');
        const state = adapter.read();
        if (state.readOnly || state.busy) throw new Error('Форма недоступна для редактирования');
        const fields = await this.fields(formId, state.values, state, signal);
        const patch = validateAiFormValues(fields, values);
        for (const field of fields) {
            if (Object.prototype.hasOwnProperty.call(patch, field.key) && state.editableFields
                && !state.editableFields.includes(field.modelKey ?? field.key)) throw new Error(`Поле ${field.key} недоступно`);
        }
        const modelValues = await writeAiFormValues(fields, patch, state, state.values, false, signal);
        if (signal?.aborted) throw new Error('Вызов инструмента отменён');
        if (this.adapter() !== adapter) throw new Error('Форма уже закрыта или страница изменилась');
        await adapter.patch(modelValues);
        this.fillError = null;
        this.saveCurrent();
        return this.snapshot();
    }

    async prepare(formId: string, items: unknown, toolCallId: string, signal?: AbortSignal, force = false,
        cloneActiveBudget?: boolean): Promise<PreparedAiForms> {
        const form = getAiForm(formId);
        if (cloneActiveBudget !== undefined && !form.resolveCreatePath) throw new Error('Эта форма не поддерживает клонирование активного бюджета');
        if (!(await loadGrantedPermissions()).has(form.permission)) throw new Error('Нет права создавать объекты этой формы');
        const maxItems = Math.min(form.maxPrepareItems ?? 100, 100);
        if (!Array.isArray(items) || !items.length || items.length > maxItems) {
            throw new Error(`items должен быть непустым массивом; максимум элементов: ${maxItems}`);
        }
        const requests: AiFormRequests = new Map();
        if (form.prepareItemsField) {
            const input = { [form.prepareItemsField]: items };
            const fields = await this.fields(formId, input, undefined, signal, requests);
            const values = validateAiFormValues(fields, input);
            const previous = this.preparedTables.get(toolCallId);
            if (previous) {
                if (this.adapter() !== previous) throw new Error('Подготовленная форма уже закрыта');
                return { mode: 'table', form: await this.snapshot() };
            }
            const resolvedPath = form.resolveCreatePath ? await this.resolveCreationRoute(formId, values, signal, undefined, false, requests) : null;
            const target = resolvedPath ? new URL(resolvedPath.path, window.location.origin) : null;
            if (!isAiFormPage(form) || (!form.matchesPage && target
                && (target.pathname !== window.location.pathname || target.hash !== window.location.hash))
                || (force && this.adapter() && new URL(window.location.href).searchParams.get(AI_FORM_CALL_QUERY) !== toolCallId)) {
                const route = target ?? new URL((await this.resolveCreationRoute(formId, values, signal, undefined, false, requests)).path, window.location.origin);
                route.searchParams.delete(AI_FORM_QUERY);
                route.searchParams.set(AI_FORM_CALL_QUERY, toolCallId);
                return this.navigate(route.pathname + route.search + route.hash, signal, force);
            }
            if (this.adapter() || this.preparingTable) throw new Error('Форма уже открыта или открывается; используйте get_current_form и update_current_form');
            const open = this.openers.get(formId);
            if (!open) throw new Error('Страница не предоставила открытие формы');
            if (signal?.aborted) throw new Error('Вызов инструмента отменён');
            this.preparingTable = true;
            try {
                await open();
                const adapter = await this.waitForAdapter(formId, signal);
                const result = await this.patch(formId, values, signal);
                this.preparedTables.set(toolCallId, adapter);
                return { mode: 'table', form: result };
            } finally { this.preparingTable = false; }
        }
        const values = await Promise.all(items.map(async item => {
            const fields = await this.fields(formId, item, undefined, signal, requests);
            return validateAiFormValues(fields, item);
        }));

        if (values.length === 1) {
            const previous = this.preparedSingles.get(toolCallId);
            if (previous) {
                if (this.adapter() !== previous) throw new Error('Подготовленная форма уже закрыта');
                return { mode: 'single', form: await this.snapshot(), ...(cloneActiveBudget ? { reviewRequired: true } : {}) };
            }
            const resolvedPath = form.resolveCreatePath ? await this.resolveCreationRoute(formId, values[0], signal, cloneActiveBudget, false, requests) : null;
            const target = resolvedPath ? new URL(resolvedPath.path, window.location.origin) : null;
            if (new URL(window.location.href).searchParams.get(AI_FORM_CALL_QUERY) !== toolCallId
                || (form.matchesPage ? !isAiFormPage(form) : target
                    && (target.pathname !== window.location.pathname || target.hash !== window.location.hash))) {
                const route = target ?? new URL((await this.resolveCreationRoute(formId, values[0], signal, cloneActiveBudget, false, requests)).path, window.location.origin);
                route.searchParams.delete(AI_FORM_QUERY);
                route.searchParams.set(AI_FORM_CALL_QUERY, toolCallId);
                // Ответ остаётся неотправленным: после перехода сервер повторит этот вызов с теми же аргументами.
                return this.navigate(route.pathname + route.search + route.hash, signal, force);
            }
            const adapter = await this.waitForAdapter(formId, signal);
            if (adapter.read().entityId !== null) throw new Error('Открыта форма существующего объекта');
            this.creationCallIds.set(adapter, toolCallId);
            if (resolvedPath?.reviewRequired) {
                this.preparedSingles.set(toolCallId, adapter);
                return { mode: 'single', form: await this.snapshot(), reviewRequired: true };
            }
            const result = await this.patch(formId, values[0], signal);
            this.preparedSingles.set(toolCallId, adapter);
            return { mode: 'single', form: result };
        }

        const routes = form.resolveCreatePath
            ? await Promise.all(values.map(async fields => (await this.resolveCreationRoute(formId, fields, signal, cloneActiveBudget, false, requests)).path))
            : Array(values.length).fill((await this.resolveCreationRoute(formId, values[0], signal, undefined, false, requests)).path);
        const drafts = values.map((fields, index) => {
            const id = `${toolCallId}-${index}`;
            const existing = this.drafts.get(id);
            if (existing) return existing;
            try {
                return this.drafts.create(formId,
                    [fields['lastName'], fields['firstName']].filter(Boolean).join(' ') || `${form.title} ${index + 1}`,
                    routes[index], fields, id);
            } catch (error) {
                console.error('Не удалось сохранить черновик формы в браузере', error);
                throw error;
            }
        });
        return { mode: 'multiple', drafts };
    }

    private async resolveCreationRoute(formId: string, values: Record<string, unknown>, signal?: AbortSignal,
        cloneActiveBudget?: boolean, checkOnly = false, requests?: AiFormRequests): Promise<AiFormCreationTarget> {
        const form = getAiForm(formId);
        const adapter = this.adapter();
        const state = adapter?.form === formId ? adapter.read() : undefined;
        const target = form.resolveCreatePath ? await form.resolveCreatePath(values, { state, signal, cloneActiveBudget, checkOnly, requests })
            : { path: form.createPath };
        const requested = new URL(target.path, window.location.origin);
        if (requested.origin !== window.location.origin) throw new Error('Страница создания недоступна');
        // Динамический обработчик уже проверил доступ; redirect выполняет браузер, сохраняя hash.
        if (form.resolveCreatePath) return { ...target, path: requested.pathname + requested.search + requested.hash };
        // Штатный маршрут может уточнить контекст формы через redirect.
        const response = await fetch(requested.href, { credentials: 'same-origin', signal });
        const path = new URL(response.url);
        if (!response.ok || path.origin !== window.location.origin || /login/.test(path.pathname)) throw new Error('Страница создания недоступна');
        return { path: path.pathname === requested.pathname ? requested.pathname + requested.search + requested.hash : path.pathname + path.search + path.hash };
    }

    private navigate(route: string, signal?: AbortSignal, force = false): Promise<never> {
        if (signal?.aborted) return Promise.reject(new Error('Вызов инструмента отменён'));
        const state = this.adapter()?.read();
        if (force && (state?.busy || this.preparingTable)) return Promise.reject(new Error('Форма занята; дождитесь завершения операции'));
        if (!force && state?.entityId === null) {
            return Promise.reject(new Error('Уже открыта несохранённая форма; используйте get_current_form и update_current_form'));
        }
        return new Promise((_, reject) => {
            const fail = (error: Error) => {
                clearTimeout(timer);
                signal?.removeEventListener('abort', aborted);
                reject(error);
            };
            const aborted = () => fail(new Error('Вызов инструмента отменён'));
            const timer = setTimeout(() => fail(new Error('Не удалось перейти на форму; проверьте подтверждение ухода со страницы')), FORM_WAIT_MS);
            signal?.addEventListener('abort', aborted, { once: true });
            try { window.location.assign(route); }
            catch (error) { fail(error); }
        });
    }

    private waitForAdapter(formId: string, signal?: AbortSignal): Promise<AiFormAdapter> {
        if (signal?.aborted) return Promise.reject(new Error('Вызов инструмента отменён'));
        return new Promise((resolve, reject) => {
            const cleanup = () => {
                clearTimeout(timer);
                document.removeEventListener('cynteka-ai-form-ready', check);
                signal?.removeEventListener('abort', aborted);
            };
            const fail = (error: Error) => { cleanup(); reject(error); };
            const aborted = () => fail(new Error('Вызов инструмента отменён'));
            const check = () => {
                try {
                    const adapter = this.adapter();
                    if (adapter?.form === formId && adapter.read().ready !== false) {
                        cleanup();
                        resolve(adapter);
                    }
                } catch (error) { fail(error); }
            };
            const timer = setTimeout(() => fail(new Error('Страница не предоставила готовую форму для заполнения')), FORM_WAIT_MS);
            document.addEventListener('cynteka-ai-form-ready', check);
            signal?.addEventListener('abort', aborted, { once: true });
            check();
        });
    }

    open(id: string): void {
        const draft = this.drafts.get(id);
        if (!draft) throw new Error('Черновик недоступен или устарел');
        if (this.activeDraft?.id === id && this.adapter()) return;
        window.location.assign(draft.route);
    }

    private readonly restore = (): Promise<void> => {
        if (this.restoreTask) return this.restoreTask;
        const id = new URL(window.location.href).searchParams.get(AI_FORM_QUERY);
        const adapter = this.adapter();
        if (!id || !adapter || adapter === this.applied || adapter.read().ready === false) return Promise.resolve();
        this.restoreTask = (async () => {
            try {
                const draft = this.drafts.get(id);
                if (!draft || draft.form !== adapter.form) throw new Error('Черновик недоступен или устарел');
                const target = new URL(draft.route, window.location.origin);
                const definition = getAiForm(draft.form);
                const matchesPage = definition.matchesPage ? isAiFormPage(definition)
                    : target.pathname === window.location.pathname && target.hash === window.location.hash;
                if (target.origin !== window.location.origin || !matchesPage
                    || adapter.read().entityId !== null) throw new Error('Черновик предназначен для другой страницы');
                await this.patch(draft.form, draft.values);
                this.applied = adapter;
                this.activeDraft = draft;
                draft.visitedAt = Date.now();
                this.saveCurrent();
            } catch (error) { this.fillError = error.message; }
        })().finally(() => { this.restoreTask = null; });
        return this.restoreTask;
    };

    private readonly saveCurrent = async (): Promise<void> => {
        const adapter = this.adapter();
        if (!this.activeDraft || !adapter || adapter.form !== this.activeDraft.form) return;
        try {
            const draft = this.activeDraft;
            const values = await this.readValues(adapter);
            if (this.adapter() !== adapter || this.activeDraft !== draft) return;
            draft.values = values;
            this.drafts.save(draft);
        } catch (error) { console.error('Не удалось сохранить черновик формы в браузере', error); }
    };
}
